import type { ReactNode } from "react";

import { FrontendErrorBoundary } from "@actionary/frontend-substrate";

/**
 * Portal-app composition of the shared FrontendErrorBoundary. The
 * substrate (packages/frontend-substrate) owns render-error capture,
 * reporting, and the recovery-shell scaffold; this file paints the
 * shell in the portal's visual vocabulary (slate-50 ground, slate-900
 * card in dark theme) and composes the two visitor-facing recovery
 * actions.
 *
 * Portal visitors never see stack traces — `showDetails` stays off so
 * a broken render on a client's screen carries the operator-side
 * "team notified" line without ever surfacing internals.
 */
export function PortalErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <FrontendErrorBoundary
      classNames={{
        container:
          "min-h-screen flex items-center justify-center p-6 bg-slate-50 dark:bg-slate-950",
        card:
          "max-w-md w-full rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 space-y-3 shadow-sm",
        heading: "text-lg font-medium text-slate-900 dark:text-slate-100",
        paragraph: "text-sm text-slate-600 dark:text-slate-400",
        actionRow: "flex gap-2 flex-wrap pt-2",
      }}
      actions={
        <>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-md bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 font-medium"
          >
            Reload
          </button>
          <button
            type="button"
            onClick={() => window.location.assign("/")}
            className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-md border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300"
          >
            Go home
          </button>
        </>
      }
    >
      {children}
    </FrontendErrorBoundary>
  );
}
